/**
 * Petits composants de Construire ⑥ (lot U6b, vague 5) : carte en brouillon (ambre), motif d'un
 * refus AVANT le clic avec son code, mention 0.5.x visible (jamais un retrait silencieux) et
 * carte « Orchestration des agents : où la voir » (planche MisOrchestration).
 */
import type { JSX, ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { useT } from '@/i18n/language'
import { LIEN_LISTE } from '@/lib/cibles'

/** QST-F-03 : chaque écran qui cite une carte de caste la dit brouillon, sans reçu (ambre). */
export interface EtatCarte { status?: string | null | undefined; version?: string | null | undefined; model?: string | null | undefined }

export function CarteBrouillon({ carte, etat }: { carte: string; etat?: EtatCarte | undefined }): JSX.Element {
  const { t } = useT()
  if (etat?.status === 'active') {
    // S27 : la passerelle dit la carte EN SERVICE ; la validité du reçu n'est pas servie, l'écran ne l'affirme pas.
    return (
      <span className="inline-flex flex-wrap items-center gap-1 text-xs" data-testid="carte-brouillon">
        <code>{carte}</code>
        <span className="rounded border px-1.5 py-0.5 font-semibold">
          {[t('construire.carteEnService', 'en service'), etat.version, etat.model].filter(Boolean).join(' · ')}
        </span>
      </span>
    )
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-1 text-xs" data-testid="carte-brouillon">
      <code>{carte}</code>
      <span className="rounded border border-[var(--color-warning)] px-1.5 py-0.5 font-semibold text-[var(--color-warning-text)]">
        {t('construire.carteBrouillon', 'brouillon · sans reçu de qualification')}
      </span>
    </span>
  )
}

/** Liste des cartes citées, avec la note « Admis en bêta ». */
export function CartesBrouillon({ cartes, etats }: {
  cartes: string[]
  /** État servi par la passerelle, par carte (S27) ; absent = toutes présumées brouillon. */
  etats?: Readonly<Record<string, EtatCarte | undefined>> | undefined
}): JSX.Element | null {
  const { t } = useT()
  if (cartes.length === 0) return null
  const toutesEnService = cartes.every((c) => etats?.[c]?.status === 'active')
  return (
    <div className={`space-y-1 rounded-md border border-dashed p-3${toutesEnService ? '' : ' border-[var(--color-warning)]'}`} data-testid="cartes-brouillon">
      <ul className="flex flex-wrap gap-3">
        {cartes.map((c) => <li key={c}><CarteBrouillon carte={c} etat={etats?.[c]} /></li>)}
      </ul>
      {toutesEnService ? null : <p className="text-xs text-muted-foreground">
        {t('construire.carteBrouillonNote', 'Admis en bêta (QST-F-03) : la qualification au banc précède la version stable, pas la bêta ; le dossier de mission le consigne. Le reçu du profil de génération ne couvre pas la carte.')}
      </p>}
    </div>
  )
}

/** Motif d'un bouton refusé avant le clic, relié par `aria-describedby`, avec son code technique. */
export function MotifRefus({ id, code, children, testid = 'refus-avant-clic' }: {
  id: string
  code: string
  children: ReactNode
  testid?: string
}): JSX.Element {
  const { t } = useT()
  return (
    <div id={id} role="note" data-testid={testid} data-code={code} className="space-y-1 text-sm text-[var(--color-error-text)]">
      <p>{children}</p>
      <details className="text-xs text-muted-foreground">
        <summary className="cursor-pointer">{t('arret.detailTechnique', 'Détail technique')}</summary>
        <code>{code}</code>
      </details>
    </div>
  )
}

/** Une capacité dessinée mais livrée après la v0.5.0 : mention visible, jamais un vide. */
export function Mention05x({ children, testid }: { children: ReactNode; testid?: string }): JSX.Element {
  return (
    <p className="rounded-md border border-dashed p-2 text-xs text-muted-foreground" data-testid={testid ?? 'mention-05x'}>
      <span className="mr-2 rounded border px-1 font-semibold">0.5.x</span>{children}
    </p>
  )
}

/**
 * « Orchestration des agents : où la voir » (remarque 5 de l'utilisateur) : le harnais, la
 * supervision, l'instanciation et l'orchestration ont chacun leur écran ; cette carte les relie.
 * Sans mission, les liens propres à une mission mènent à la liste.
 */
export function CarteOrchestration({ missionId }: { missionId: string | null }): JSX.Element {
  const { t } = useT()
  const base = missionId ? `/missions?mission=${encodeURIComponent(missionId)}` : '/missions'
  const liens: [string, string, string][] = [
    ['plan', base, t('construire.orch.plan', 'Plan de la reine (tâches, castes, critères)')],
    ['instanciation', missionId ? `${base}&vue=instanciation` : base, t('construire.orch.instanciation', 'Instanciation par tâche (carte, modèle, contexte)')],
    ['pilotage', base, t('construire.orch.pilotage', 'Poste de pilotage : graphe des tâches, castes du harnais')],
    ['supervision', '/supervision', t('construire.orch.supervision', 'Supervision des agents du projet')],
    ['registre', missionId ? `${base}#registre` : base, t('construire.orch.registre', 'Registre du tableau noir')],
  ]
  return (
    <section className="rounded-lg border p-4" aria-labelledby="orch-titre" data-testid="carte-orchestration">
      <h2 id="orch-titre" className="text-base font-semibold">{t('construire.orch.titre', 'Orchestration des agents : où la voir')}</h2>
      <p className="mb-2 text-sm text-muted-foreground">
        {t('construire.orch.aide', 'Le harnais, la surveillance des agents, l’instanciation et l’orchestration ont chacun leur écran ; cette console n’exécute que les agents personnalisés, hors colonie.')}
      </p>
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
        {liens.map(([cle, vers, libelle]) => (
          <li key={cle}>
            <Link to={vers} data-testid={`orch-${cle}`} className={`${LIEN_LISTE} text-brand-text`}>{libelle}</Link>
          </li>
        ))}
      </ul>
      {!missionId ? (
        <p className="mt-2 text-xs text-muted-foreground">{t('construire.orch.sansMission', 'Aucune mission active : le plan, l’instanciation et le registre s’ouvrent depuis la liste des missions.')}</p>
      ) : null}
    </section>
  )
}
